<template>
  <div id="__Preferences">
    <PageTitle :title="$t('Preferences')" />

    <div class="q-mt-md">
      <Panel>
        <Splitter :value="splitterModel">
          <template v-slot:before>
            <CTabs v-model="currentTab" :tab-items="tabItems" vertical inlineLabel />
          </template>

          <template v-slot:after>
            <TabPanels v-model="currentTab" class="q-px-md">
              <TabPanel name="my-account">
                <MyAccount />
              </TabPanel>

              <TabPanel name="sub-user">
                <SubUserManagement />
              </TabPanel>

              <TabPanel name="accessToken">
                <UserClients />
              </TabPanel>

              <TabPanel name="identityProvider">
                <IdentityProviderSettings />
              </TabPanel>

              <TabPanel name="notification">
                <NotificationSettings />
              </TabPanel>

              <TabPanel name="budget-alert">
                <BudgetAlerts />
              </TabPanel>
            </TabPanels>
          </template>
        </Splitter>
      </Panel>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import TabPanels from '@design-system/components/TabPanels.vue';
import TabPanel from '@design-system/components/TabPanel.vue';
import Tabs from '@design-system/components/Tabs.vue';
import PageTitle from '@design-system/components/PageTitle.vue';
import Splitter from '@design-system/components/Splitter.vue';
import Panel from '@design-system/components/Panel.vue';
import CTabs from '@/components/uikit/CTabs';
import MyAccount from './MyAccount';
import SubUserManagement from './SubUserManagement';
import UserClients from './UserClient';
import IdentityProviderSettings from './IdentityProvider';
import NotificationSettings from './Notification';
import BudgetAlerts from './BudgetAlerts/BudgetAlerts.vue';

const messages = {
  en: {
    Preferences: 'Preferences',
    'My account': 'My Account',
    'Sub user': 'Sub User Management',
    'API Access Tokens': 'API Access Tokens',
    'Identity Provider': 'Identity Provider',
    Notification: 'Notifications and Alerts',
    'Budget Alerts': 'Budget Alerts',
  },
  ja: {
    Preferences: '環境設定',
    'My account': 'マイアカウント',
    'Sub user': 'サブユーザーの管理',
    'API Access Tokens': 'API アクセストークン',
    'Identity Provider': 'IdP設定',
    Notification: '通知',
    'Budget Alerts': '予算に関するアラート',
  },
};

export default {
  name: 'Preferences',
  i18n: { messages },
  components: {
    MyAccount,
    SubUserManagement,
    UserClients,
    IdentityProviderSettings,
    NotificationSettings,
    TabPanels,
    TabPanel,
    Tabs,
    PageTitle,
    Splitter,
    Panel,
    BudgetAlerts,
    CTabs,
  },
  data() {
    return {
      currentTab: 'my-account',
      splitterModel: 250,
    };
  },
  computed: {
    ...mapGetters('rbac', {
      enabledRbacSetting: 'enabledRbacSetting',
    }),

    ...mapGetters('user', {
      isSSO: 'isSSO',
      isAccessGroup: 'isAccessGroup',
    }),

    ...mapState({
      openApiEnabled: state => state.userMeta.open_api,
    }),

    tabItems() {
      return [
        {
          label: this.$t('My account'),
          value: 'my-account',
          icon: 'person',
        },
        {
          label: this.$t('Notification'),
          value: 'notification',
          icon: 'notifications',
        },
        ...(this.$display('budgetalerts') &&
        (this.currentTab === 'notification' || this.currentTab === 'budget-alert')
          ? [
              {
                label: this.$t('Budget Alerts'),
                value: 'budget-alert',
                icon: 'notification_important',
                class: 'ml-md',
              },
            ]
          : []),
        ...(this.enabledRbacSetting && this.$display('users_management')
          ? [
              {
                label: this.$t('Sub user'),
                value: 'sub-user',
                icon: 'supervisor_account',
              },
            ]
          : []),
        ...(this.openApiEnabled && !this.isSSO
          ? [
              {
                label: this.$t('API Access Tokens'),
                value: 'accessToken',
                icon: 'password',
              },
            ]
          : []),
        ...(this.$can('Admin')
          ? [
              {
                label: this.$t('Identity Provider'),
                value: 'identityProvider',
                icon: 'fingerprint',
              },
            ]
          : []),
      ];
    },
  },
  beforeMount() {
    const query = this.$route.query;
    if (query.currentTab) {
      let isExist = this.tabItems.find(item => item.value === query.currentTab);
      if (isExist) {
        this.currentTab = query.currentTab;
      }
    }
  },
};
</script>

<style lang="scss" scoped>
#__Preferences {
  padding: $space-md;
}
</style>
